import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useApiHelper from "../hooks/useApiHelper";
import { DefaultResponse, DefaultResponseWithData } from "../models/system";
import {
  ICreateShopifyProfileFromDraftRequest,
  ICreateUpdateShopifyShippingPolicyMapRequest,
  IPreviewShopifyProfileFromEbayRequest,
  IShopifyProfileDraft,
  IShopifyDeliveryProfile,
  IShopifyShippingPolicyMap,
  IShopifyShippingPolicyMapValidationResult,
  IShopifyStore,
} from "../models/ShopifyShippingPolicy";

export default function useShopifyShippingPolicySync(storeId: string | undefined) {
  const { get, post, del } = useApiHelper();
  const queryClient = useQueryClient();

  const stores = useQuery({
    queryKey: ["shopifyStores"],
    queryFn: () => get<DefaultResponseWithData<IShopifyStore[]>>("/shopify/stores"),
    staleTime: 1000 * 60 * 10,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const mappings = useQuery({
    queryKey: ["shopifyShippingPolicyMappings", storeId],
    queryFn: () =>
      get<DefaultResponseWithData<IShopifyShippingPolicyMap[]>>(
        `/shopifyshippingpolicy/mappings${storeId ? `?storeId=${storeId}` : ""}`
      ),
    staleTime: 1000 * 60,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const deliveryProfiles = useQuery({
    queryKey: ["shopifyDeliveryProfiles", storeId],
    queryFn: () =>
      get<DefaultResponseWithData<IShopifyDeliveryProfile[]>>(
        `/shopifyshippingpolicy/delivery-profiles/${storeId}`
      ),
    enabled: Boolean(storeId),
    staleTime: 1000 * 60,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const validation = useQuery({
    queryKey: ["shopifyShippingPolicyValidation", storeId],
    queryFn: () =>
      get<DefaultResponseWithData<IShopifyShippingPolicyMapValidationResult[]>>(
        `/shopifyshippingpolicy/validate${storeId ? `?storeId=${storeId}` : ""}`
      ),
    staleTime: 1000 * 60,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const upsertMapping = useMutation(
    (body: ICreateUpdateShopifyShippingPolicyMapRequest) =>
      post<ICreateUpdateShopifyShippingPolicyMapRequest, DefaultResponseWithData<IShopifyShippingPolicyMap>>(
        "/shopifyshippingpolicy/mappings",
        body
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["shopifyShippingPolicyMappings"]);
        queryClient.invalidateQueries(["shopifyShippingPolicyValidation"]);
      },
    }
  );

  const deleteMapping = useMutation((mappingId: string) => del(`/shopifyshippingpolicy/mappings/${mappingId}`), {
    onSuccess: () => {
      queryClient.invalidateQueries(["shopifyShippingPolicyMappings"]);
      queryClient.invalidateQueries(["shopifyShippingPolicyValidation"]);
    },
  });

  const applyMappings = useMutation(
    (dryRun: boolean) =>
      post<{ shopifyStoreId?: string; dryRun: boolean }, DefaultResponse>("/shopifyshippingpolicy/apply-mappings", {
        shopifyStoreId: storeId,
        dryRun,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["shopifyShippingPolicyValidation"]);
      },
    }
  );

  const previewProfileFromEbay = useMutation(
    (body: IPreviewShopifyProfileFromEbayRequest) =>
      post<IPreviewShopifyProfileFromEbayRequest, DefaultResponseWithData<IShopifyProfileDraft>>(
        "/shopifyshippingpolicy/preview-map",
        body
      )
  );

  const createProfileFromDraft = useMutation(
    (body: ICreateShopifyProfileFromDraftRequest) =>
      post<ICreateShopifyProfileFromDraftRequest, DefaultResponse>("/shopifyshippingpolicy/create-profile", body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["shopifyDeliveryProfiles"]);
        queryClient.invalidateQueries(["shopifyShippingPolicyMappings"]);
        queryClient.invalidateQueries(["shopifyShippingPolicyValidation"]);
      },
    }
  );

  return {
    stores,
    mappings,
    deliveryProfiles,
    validation,
    upsertMapping,
    deleteMapping,
    applyMappings,
    previewProfileFromEbay,
    createProfileFromDraft,
  };
}
